import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import noop from '@client/utils/noop'

const mergeRefs =
  (...refs) =>
  (node) => {
    refs.forEach((ref) => {
      if (!ref) {
        return
      }

      if (typeof ref === 'function') {
        ref(node)
      } else {
        ref.current = node
      }
    })
  }

const EventWrapper = ({ children, onMouseLeave = noop, onMouseOver = noop, ref: externalRef }) => {
  const [domElement, setDomElement] = useState(null)

  // Validate children prop
  if (!children || !React.isValidElement(children)) {
    console.error('EventWrapper: children prop must be a valid React element')
    return null
  }

  const refCallback = useCallback((ref) => {
    if (!ref) {
      setDomElement(null)
      return
    }

    let element = null

    // Handle different ref types
    if (ref instanceof HTMLElement) {
      // Direct DOM element
      element = ref
    } else if (ref && typeof ref === 'object') {
      // Try common patterns for component refs
      if (ref.current instanceof HTMLElement) {
        // useRef pattern
        element = ref.current
      } else if (ref.getDOMElement && typeof ref.getDOMElement === 'function') {
        // useImperativeHandle pattern
        element = ref.getDOMElement()
      } else if (ref._reactInternals || ref._reactInternalInstance) {
        // Class component instance - try to find first DOM child
        const node = ref._reactInternals?.child?.stateNode || ref._reactInternalInstance?.child?.stateNode
        if (node instanceof HTMLElement) {
          element = node
        }
      }
    }

    setDomElement(element)
  }, [])

  useEffect(() => {
    if (!domElement || !domElement.addEventListener) return

    const addCustomValues = (event) => {
      event.track = event.track || []
      event.track.push(domElement)
      event.listenerElement = domElement
    }

    const handleMouseLeave = (event) => {
      addCustomValues(event)
      onMouseLeave(event)
    }

    const handleMouseOver = (event) => {
      addCustomValues(event)
      onMouseOver(event)
    }

    // Add event listeners
    if (onMouseLeave !== noop) {
      domElement.addEventListener('mouseleave', handleMouseLeave, false)
    }
    if (onMouseOver !== noop) {
      domElement.addEventListener('mouseover', handleMouseOver, false)
    }

    // Cleanup function
    return () => {
      if (domElement.removeEventListener) {
        domElement.removeEventListener('mouseleave', handleMouseLeave, false)
        domElement.removeEventListener('mouseover', handleMouseOver, false)
      }
    }
  }, [domElement, onMouseLeave, onMouseOver])

  return React.cloneElement(children, {
    ref: mergeRefs(refCallback, children.ref, externalRef),
  })
}

EventWrapper.propTypes = {
  children: PropTypes.element.isRequired,
  onMouseLeave: PropTypes.func,
  onMouseOver: PropTypes.func,
}

export default EventWrapper
